type DateAny = number | string | Date;

export enum Language {
    FR = 'fr',
    EN = 'en',
}

export interface BaseUser {
    id: string;
    id_core?: string;
    firstname: string;
    lastname: string;
    phone_number: string;
    email?: string;
    status?: UserStatus;
    user_type?: string;
}
export interface User extends BaseUser {
    companies?: Company[];
}

export interface UserCompany {
    name: string;
    id: string;
    account_details: {
        phone_number: string;
        email: string;
    };
}

export enum UserStatus {
    REGISTRED = 'REGISTRED',
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    BANNED = 'BANNED',
}

export interface Driver extends BaseUser {
    note: number;
    partner: Partner;
    picture?: string;
}

export interface PromoCode {
    id: string;
    name: string;
    description: string;
    status: PromoCodeStatus;
    expiration_date: number;
    created_at: number;
    use_limit?: number;
}

export interface Promotion {
    id: number;
    desc: string; // description
    amount: string; // xx € or xx%
    code: string; // The promo name, actually
    display_order: number;
    is_active?: boolean;
    use_limit?: number;
    usage?: number;
    expiry_date?: number;
}

export enum PromoCodeStatus {
    APPLIED = 'APPLIED',
    REVOKED = 'REVOKED',
    INVALID = 'INVALID',
}

export interface SearchAddress {
    details: AddressDetails;
    location: Location;
    name?: string;
}

export interface AutoCompleteAddress {
    details: { name: string; full_name: string };
    place_id: string;
    location: Location;
    categories: any;
}

export interface Address {
    name?: string;
    location: Location;
    type: AddressType;
    details: AddressDetails;
    meeting_points?: MeetingPoint[];
    meeting_points_nameBoard?: MeetingPoint[];
    poi?: POI;
    poi_id?: number;
}

export enum AddressType {
    FAVORITE = 'FAVORITE',
    HOME = 'HOME',
    WORK = 'WORK',
    ADDRESS = 'ADDRESS', // default
    AIRPORT = 'AIRPORT',
    STATION = 'STATION',
    RECENT = 'RECENT',
    OTHER = 'OTHER',
}

export enum RoleType {
    PRINCIPAL = 'PRINCIPAL',
    RIDER = 'RIDER',
    BOOKER = 'BOOKER',
}

export enum MeetingPointType {
    ENTRANCE = 'ENTRANCE',
    LOCATION = 'LOCATION',
    TERMINAL = 'TERMINAL',
    STREET = 'STREET',
}

export interface Location {
    lat: number;
    lng: number;
}

export interface AddressDetails {
    street_number: string;
    street: string;
    city: string;
    country: string;
    zip_code: string;
    full_name: string;
}

export interface BaseStop {
    address?: Address;
    stop_type: StopType;
}

export interface Stop extends BaseStop {
    eta: number;
    connection_details: ConnectionDetails;
    waiting_time: number;
    payload: Payload[];
    meeting_point_id?: number;
    stop_order: number;
    poi?: POI;
    driver_arrival_time?: number;
    driver_departure_time?: number;
}

export interface PoiStop extends BaseStop {
    meeting_point_id?: number;
    connection_details?: Pick<ConnectionDetails, 'number'>;
}

export interface Payload {
    type: PayloadType;
    id: string;
}

export enum PayloadType {
    RIDER = 'RIDER',
    ITEM = 'ITEM',
}

export enum StopType {
    DRIVER_ARRIVAL = 'DRIVER_ARRIVAL',
    PICKUP = 'PICKUP',
    STOP = 'STOP',
    DROP_OFF = 'DROP_OFF',
}

interface ConnectionDetails {
    number: string;
    company: string;
    connection_from: string;
    connection_time: string;
    has_arrived: boolean;
    meeting_point: boolean;
    connection_time_delay: number;
    connection_area: string; // terminal // quais...
    type: string;
}

export interface Favorite extends Address {
    uuid: string;
}

export interface Area {
    type: 'circle' | 'polygon';
    radius: number;
    center: Location;
}

export interface MeetingPoint {
    id: number;
    location: Location;
    name: string;
    rdv_point: string;
    type: MeetingPointType;
}
export interface POI {
    area: Area;
    force_selection: boolean;
    poi_id: number;
    details: AddressDetails;
    location: Location;
    meeting_points: MeetingPoint[];
    meeting_points_nameBoard?: MeetingPoint[];
    name: string;
    type: AddressType;
}

export enum AccountType {
    PRO = 'Pro',
    PERSO = 'Perso',
}

export interface Ride {
    id: string;
    number: string;
    is_asap: boolean;
    is_assistance: boolean;
    is_free_ride: boolean;
    // options: Options[];

    // Dates
    creation_date: number;
    start_date: number;
    planned_start_date: number;
    cancellation_date: number;
    ride_accepted_date: number;
    ride_ended_date: number;

    stops: Stop[];
    status: Status;
    // LT-1877 : verdict paiement, distinct du statut de course. Les deux se
    // lisent ensemble — BLOCKED reste fige apres une annulation, ce qui est la
    // regle de detection d'une annulation causee par le paiement (LT-2056).
    payment_clearance_status?: PaymentClearanceStatus | null;
    emergency: Emergency;
    cancelled_by?: Author;
    cancel_reason: Reasons;
    current_issue: Reasons;

    booker: User;
    riders: BaseUser[];
    driver: Driver;
    partner: Partner;
    driver_tracking?: DriverTracking;
    vehicle: Vehicle;
    price_details: PriceDetails;
    actions_history: ActionHistory[];
    service: Service;
    subcontract?: Subcontract;
    should_subcontract: boolean;
    provision: number; // in minutes for MAD
    booking_platform: string; // ex: Web App 1.2.2
    operator_logs: OperatorLog[];
    cost_center?: CostCenter;
    driver_note?: string;
    ride_reason?: string;
    billing?: Billing;

    payment_method: PaymentMethod;
    payment_policy: PaymentGroupSetting;
    group: Pick<IDGroup, 'uuid' | 'name' | 'contract'>;
    nameboard_option?: boolean;
    driver_rating?: DriverRating;
    encoded_route?: string;
    route?: RouteInfos;

    // Recurring
    schedule?: string; // cron string
    recurring_id?: string; // Recurrence from
    breaks: Break[];
}

export interface RouteInfos {
    path: string;
    distance: number; // distance du tracé en m
}

export interface Break {
    id: string;
    recurring_id: string;
    start_date: number; // Timestamp
    end_date: number; // Timestamp
    creation_date: number; // Timestamp
    deleted_at?: number; // Timestamp
}

export interface Billing {
    id: string;
    status: 'issued' | 'ready' | string;
    type: string;
    with_tax_customer: number;
    pre_tax_customer: number;
    distance: number;
}

export interface DriverRating {
    score: number;
    comment: string;
    tags: string[];
}

export interface DriverTracking {
    eta: number; // in seconds
    driver_id: string;
    location: {
        lat: number;
        lng: number;
        timestamp: number;
    };
    heading: number; // float (0-360°)
}

export interface Author {
    role: Role;
    user: BaseUser;
}

export enum Role {
    ADMIN = 'ADMIN',
    OPERATOR = 'OPERATOR',
    DRIVER = 'DRIVER',
    CLIENT = 'CLIENT',
    SYSTEM = 'SYSTEM',
}

export interface ActionHistory {
    date: DateAny;
    event_id?: string;
    triggered_by: BaseUser;
    resulting_status: Status;
    action_origin: string; // "RIDE_MANAGER" / "SCOM" etc
    details: any;
}

export interface PriceDetails {
    currency: string;
    client_final_price: number | null; // PRIX TOUT COMPRIS (promo, tip, toll)
    client_pre_tax_price: number | null; // Prix sans tax
    base_price: number | null; // PRIX INITIAL avant promo
    voucher?: Voucher;
    extras?: Extra[]; // le détails des tips, tolls
    promotion?: PromoLight; // LA PROMO, pareil qu'avant mais rangés ici
    pricing_request_id: string;
    pricing_response_id: string | null;
    core_price_confirmation_id?: number | null;
    covered_by_third_party: { amount_with_tax: number };
    surge: number;
}

export enum ServiceStatus {
    AVAILABLE = 'AVAILABLE',
    SERVICE_CLOSED = 'SERVICE_CLOSED',
    NO_DRIVER_AVAILABLE = 'NO_DRIVER_AVAILABLE',
    TOO_FAR_AWAY = 'TOO_FAR_AWAY',
    REGION_NOT_SERVED = 'REGION_NOT_SERVED',
    RIDER_RESTRICTED = 'RIDER_RESTRICTED',
    OTHER = 'OTHER',
    // from pricing :
    INSUFFICIENT_DELAY = 'INSUFFICIENT_DELAY',
    NO_PRICING_FOR_AREA = 'NO_PRICING_FOR_AREA',
    PREBOOK_NOT_ALLOWED = 'PREBOOK_NOT_ALLOWED',
    ASAP_NOT_ALLOWED = 'ASAP_NOT_ALLOWED',
    NO_PRICING = 'NO_PRICING',
}

export interface Display {
    disabled: boolean;
    display_name?: string;
    display_order: number;
    default_selection: boolean;
    ref_message?: string;
    values_message?: any;
}

export interface Service {
    id: string;
    uuid: string;
    name: string;
    type: ServiceType;
    carry_settings: PaxSettings | CargoSettings;
    display?: Display;
    description: string;
    is_available: boolean;
    service_id?: string;
    status?: ServiceStatus;
    book_for_later_allowed: boolean;
    selectable_by_rider: boolean;
}

export interface Voucher {
    id: string;
    name: string;
    ceiling: string;
    percentage: string;
    description: string;
    voucher_id?: string;
}

// Prise en charge accordée sur le devis (renvoyée au niveau de la réponse /quotes, pas du quote).
// En montant fixe elle porte le montant (pre_tax/with_tax) ; en pourcentage seulement le taux
// (value), le montant dépendant du prix de chaque service : il est lu dans payment_breakdown.
export interface QuoteVoucher {
    voucher_id?: string;
    name?: string;
    value_type?: 'fixed' | 'percentage';
    value?: number; // montant TTC en euros (fixed) ou taux en % (percentage)
    pre_tax?: number; // prise en charge HT, ex. 27.27 (fixed seulement)
    with_tax?: number; // prise en charge TTC, ex. 30 (fixed seulement)
}

// Répartition du prix entre payeurs, calculée par ride-manager pour chaque quote. Le payeur
// plafonné (coverage non nul) est la part prise en charge par l'entreprise, déjà plafonnée au
// prix de la course : c'est ce montant qui est facturé.
export interface PaymentBreakdownPayer {
    role: 'COMPANY' | 'RIDER';
    coverage: { type: 'percent' | 'amount'; value: number; ceiling: number | null } | null;
    amount: number | null; // null tant que le prix n'est pas connu (course gratuite, MAD)
}

export interface PaymentBreakdown {
    total: number | null;
    payers: PaymentBreakdownPayer[];
}

export interface Quote {
    id: string;
    booker_id: string;
    booker: IDEmployee;
    company_id: string;
    group: Pick<IDGroup, 'uuid' | 'name' | 'contract'>;
    expiracy_date: number; // millisecond (deprecated)
    expiry_date: number; // second (Unix timestamp)
    estimate_eta?: number;
    is_asap: boolean;
    is_assistance: boolean;
    is_free_ride: boolean;
    service: Service;
    stops: Stop[];
    price_details: PriceDetails;
    price_quotation: any; // complex structure omited cause unused
    nameboard_option?: ExtraOption;
    elligible_vouchers?: Voucher[];
    payment_breakdown?: PaymentBreakdown;
    // Ajouté côté front à partir de GetServicesOutput.vouchers, pour que la prise en charge
    // suive le quote dans le store et les écrans suivants (cf. applyVoucherToQuotes)
    applied_voucher?: QuoteVoucher;
}

export interface ExtraOption {
    amount: number;
    extra_id: string;
}

export interface PromoLight {
    promo_amount: number;
    promo_name: string;
    promo_user_id?: number;
}

export interface PaxSettings {
    max_passenger: number;
    max_small_luggage: number;
    max_large_luggage: number;
}

export interface CargoSettings {
    max_weight: number;
    max_length: number;
    max_width: number;
    max_height: number;
}

export enum ServiceType {
    PAX = 'PAX',
    CARGO = 'CARGO',
}

/**
 * LT-1877 — verdict de billing sur le paiement de la course, mirroir du
 * statut de clearance. `null`/absent = course hors perimetre (le moteur ne
 * l'a pas traitee) : aucune UI ne doit se declencher la-dessus.
 */
export enum PaymentClearanceStatus {
    READY = 'READY',
    PREAUTH_PENDING = 'PREAUTH_PENDING',
    VALIDATED = 'VALIDATED',
    BLOCKED = 'BLOCKED',
}

export enum Status {
    CREATED = 'CREATED',
    READY_TO_DISPATCH = 'READY_TO_DISPATCH',
    DISPATCH_TIMEOUT = 'DISPATCH_TIMEOUT',
    PROCESSING = 'PROCESSING', //  handled by dispatch algo
    // ALLOCATED = 'ALLOCATED', // driver affected
    RECEIVED = 'RECEIVED', // received by driver app
    // SEEN = 'SEEN', // seen by driver new ride screen
    ACCEPTED = 'ACCEPTED', // equivalent to ON_WAY
    // ON_WAY = 'ON_WAY',
    AT_PICKUP = 'AT_PICKUP',
    PASSENGER_ON_BOARD = 'PASSENGER_ON_BOARD',
    AT_DROP_OFF = 'AT_DROP_OFF',
    FINISHED = 'FINISHED',
    // CANCELLED = 'CANCELLED',
    CANCELLATION_NO_CHARGE = 'CANCELLATION_NO_CHARGE',
    CANCELLATION_WITH_CHARGE = 'CANCELLATION_WITH_CHARGE',
}

export enum Reasons {
    NO_DRIVER_AVAILABLE = 'NO_DRIVER_AVAILABLE',
    UNREACHABLE_CLIENT = 'UNREACHABLE_CLIENT',
    DRIVER_LATE = 'DRIVER_LATE',
    DRIVER_VERY_LATE = 'DRIVER_VERY_LATE',
}

export type PaymentFrequency = 'unitary' | 'monthly';
export type PaymentMode = 'centralized' | 'individual' | 'partial';
export type PaymentMethodType = 'onboard_payment' | 'card_adyen' | 'wire_transfer' | 'sepa' | 'airplus' | 'card_ogone';
export type PaymentStatus = 'active' | 'expired' | 'blocked';

export interface ContractPaymentPolicies {
    allowed_frequencies: PaymentFrequency[];
    allowed_modes: PaymentMode[];
    default_payment_method: PaymentCard;
}

export interface AdminPaymentGroupsSetting {
    contract_payment_policies: ContractPaymentPolicies;
    data: PaymentGroupSetting[];
    group_payment_policies: PaymentGroupSetting[];
    payment_methods: PaymentCard[];
}

export interface PaymentGroupSetting {
    user_id: string;
    group_id: string;
    group_name?: string;
    payment_frequency?: PaymentFrequency;
    payment_mode?: PaymentMode;
    // Only sent when the rider is set to on board payment (OBP). It is independent of payment_mode
    // and of default_payment_method, which stays the company's card.
    onboard_payment?: boolean;
    default_payment_method?: PaymentCard;
    card_fall_back?: PaymentCard;
    // LT-1841: coverage applied to the rides of the group. Required on the `partial` payment mode,
    // which also forces `payment_frequency` to 'unitary'. A group has at most one; null clears it.
    voucher_id?: string | null;
}

export interface PaymentCard {
    id: string;
    created_at: string;
    updated_at?: string;
    deleted_at?: string;
    holder_id: string;
    holder_type: string;
    name?: string;
    type: PaymentMethodType;
    status: PaymentStatus;
    payment_frequency: PaymentFrequency;
    payment_mode: PaymentMode;
    twin_card_id?: string;
    brand?: string;
    card_holder?: string;
    masked_number?: string;
    alias?: string;
    expiry_date?: string;
    card_number?: string;
    sepa_iban?: string;
    sepa_rum?: string;
    sepa_holder_name?: string;
    sepa_signature_date?: string;
}

export interface Card {
    id: string;
    name: string;
    number: string;
    validity: string;
    valid: boolean;
    default_perso: boolean;
    brand: string;
    default_pro: boolean;
}

export interface PaymentMethod {
    id?: string;
    type: PaymentMethodType;
    is_onboard_payment: boolean;
    details?: Card | PaymentCard | CostCenter;
}

export enum PaymentTypes {
    APPLE_PAY = 'APPLE_PAY',
    PAYPAL = 'PAYPAL',
    CASH = 'CASH',
    CREDIT_CARD = 'CREDIT_CARD',
    INVOICE = 'INVOICE',
}

export interface Extra {
    id: string;
    amount: number;
    description: string;
    type: ExtraType;
}

export enum ExtraType {
    TIPS = 'TIPS',
    TOLL = 'TOLL',
}

export interface Subcontract {
    id: string;
    name: string;
    status: SubcontractStatus;
}

export enum SubcontractStatus {
    PENDING = 'PENDING',
    ERROR = 'ERROR',
    SUCCESS = 'SUCCESS',
}

export interface Emergency {
    enabled: boolean;
    history: EmergencyHistory[];
}

export interface EmergencyHistory {
    trigger_date: number;
    close_date: number;
    reason: EmergencyReason;
}

export enum EmergencyReason {
    ACCIDENT = 'ACCIDENT',
    POLICE_CONTROL = 'POLICE_CONTROL',
    BREAKDOWN = 'BREAKDOWN',
    CLIENT_UNREACHABLE = 'CLIENT_UNREACHABLE',
    CANT_FIND_THE_CLIENT = 'CANT_FIND_THE_CLIENT',
}

export interface OperatorLog {
    date: DateAny;
    id: string;
    updated_by: Author;
    message: string;
}

export interface CostCenter {
    id: string;
    name: string;
    description: string;
    is_active?: boolean;
    is_default?: boolean;
}

export interface Vehicle {
    color: string;
    enabled: boolean;
    id: string;
    model: string;
    brand?: string;
    registration_number: string;
}

export interface Company {
    id: string;
    name: string;
    account_manager: Author;
    cost_centers: CostCenter[];
    account_details?: {
        phone_number: string;
        email: string;
    };
}

export interface Partner {
    id: string;
    name: string;
}

export enum Options {
    BABY_SEAT = 'BABY_SEAT',
    PET = 'PET',
    HANDICAP = 'HANDICAP',
    EXTRA_LUGGAGES = 'EXTRA_LUGGAGES',
    BRITTLE = 'BRITTLE',
}

export interface CustomerBill {
    id: string; // "a67d2ba6-83a5-421e-975c-b9083d5bd676";
    created_at: string; // "2020-10-13T06:28:40.000Z";
    customer_id: string; // "4b289e0c-497d-4ba6-abce-a4723814457e";
    type: 'ride' | 'correction' | 'exceptional' | 'month' | 'subscription';
    status: string;
    bill_number: string; // "B-202010-165511";
    amount_pre_tax: number; // 463.45;
    amount_with_tax: number; // 508.71;
    tax: number; // 508.71;
    remaining_balance: number; // 0;
    payment_method_id: string; // "f3ab0c92-2a3e-4726-8378-9303ffd4e992";
    issue_date: string; // "2020-10-13T06:28:40.000Z";
    start_date: string; // "2020-10-08T22:00:00.000Z";
    end_date: string; // "2020-10-13T22:00:00.000Z";
    user_id: string;
    group_id: string; // "21273207-6281-4668-b897-29898a04708b";
    group_name: string;
    product_type: string;
    payment_type: string;
    ride_id: string;
    ride_number: string;
    onboard_payment: boolean;
}

export interface Pagination<T> {
    data: T[];
    meta_filters?: {
        distinct_bookers?: PickerOption[];
        distinct_riders?: PickerOption[];
        distinct_groups?: PickerOption[];
        distinct_cost_centers?: PickerOption[];
    };
    meta: {
        total: number;
        current_page: number;
        pages: number;
    };
}

// ======================================= //
// ============= SelfService ============= //
// ======================================= //

export interface CompanySelfService {
    name: string;
    address: Address;
}

export interface ContractSelfService {
    contract: ContractData;
    group: Omit<GroupData, 'name' | 'headcount' | 'principals'>;
}

export interface Plan {
    entity_id: string;
    type: 'plan';
    updated_at: number;
    name: string;
    eligible_self_service: boolean;
    travel_policy_settings: {
        geo_restrictions: PlanSetting;
        calendar_restrictions: PlanSetting;
        tip_restriction: PlanSetting;
        ride_reason_obligation: PlanSetting;
        cost_center_obligation: PlanSetting;
        book_for_group_members: PlanSetting;
        book_for_group_guests: PlanSetting;
        default_pu: PlanSetting;
        default_do: PlanSetting;
        default_pu_per_user: PlanSetting;
        default_do_per_user: PlanSetting;
        favorite_addresses: PlanSetting;
    };
    allowed_dispatch_groups: string[];
    dispatch_groups: Omit<DispatchGroup, 'is_authorized'>[];
    free_users?: number;
    free_guests?: number;
    max_users?: number;
    max_guests?: number;
    max_groups?: number;
    billing: {
        allowed_frequencies: PaymentFrequency[];
        allowed_modes: PaymentMode[];
        allowed_payment_methods: payment.PaymentTypesEnum[];
        cost_centers: PlanSetting;
    };
    pricing: {
        extras?: {
            name: string;
            id: string;
            amount: number;
            percent: number;
            type_key: 'MONTHLY_SUBSCRIPTION' | 'FEE_PER_RIDER' | 'FEE_PER_GUEST';
        }[];
    };
}

export type PlanSetting = {
    allowed: boolean;
    default_value?: any;
};

// ==================================== //
// ============= DeepLink ============= //
// ==================================== //

export interface DeepLink {
    action: 'promo' | 'book' | 'selfService';
    // Promo only
    promocode?: string;
    // Book only
    pickup?: string;
    dropoff?: string;
    pickUpLat?: number;
    pickUpLng?: number;
    dropOffLat?: number;
    dropOffLng?: number;
    date?: number;
    account?: 'pro' | 'perso';
    service?: string; // 'green' | 'normal' ...
    flightNumber?: string;
    flightTerminal?: string; // id terminal
    firstname?: string;
    lastname?: string;
    mail?: string;
    phone?: string;
    // Book + Signup only
    sfirstname?: string;
    slastname?: string;
    smail?: string;
    sphone?: string;
    groupKey?: string;
    exist?: string; // To convert to boolean
    app?: string; // client name for auth
    // SelfService only
    plan?: string; // "plan1" | "plan2" plans will evolve so string will be better => plan1 noCommitment / plan2 commitment
}

export enum AppFrom {
    MARCEL = 'marcel',
}

// ==================================== //
// ============= Internal ============= //
// ==================================== //

export interface PickerOption {
    value: string;
    label: string;
    disabled?: boolean;
}

export interface SelectOption {
    id: string;
    label: string;
    disabled?: boolean;
}

// ============================== //
// ============= ID ============= //
// ============================== //
export interface IDRole {
    granted_at: string;
    expires_at?: string;
    expired_at?: string;
    name: string;
    rights: IDRight[];
    uuid: string;
}

export interface IDRoleDetail {
    tag: string;
    description: string;
    rights: IDRight[];
    uuid: string;
}

export interface IDRight {
    grant: 'R' | 'W' | string;
    tag: string;
}

export interface PlanContract {
    uuid: string;
    name: string;
    type: string;
}

export interface Contract {
    uuid: string;
    name: string;
    created_at: number;
    signed_at: number;
    plan: PlanContract;
}

export interface GroupPrincipal {
    uuid: string;
    firstname: string;
    lastname: string;
}

export interface Coworker {
    uuid: string;
    firstname: string;
    lastname: string;
    owner: boolean;
    invite: {
        status: boolean;
        invited_at?: number;
    };
    emails: [
        {
            uuid?: string;
            email: string;
        },
    ];
    phones: [
        {
            uuid: string;
            phone_e164: string;
        },
    ];
    groups: IDCoworkerGroup[];
}

export interface IDCoworkerGroup {
    uuid: string;
    name: string;
    privileges: string; // groupeA,groupeB,groupeC
}

export interface GroupData {
    uuid: string;
    name: string;
    headcount: number;
    principals: GroupPrincipal[];
}

export interface ContractData {
    uuid: string;
    headcount?: number;
}

export interface ContractGroupData {
    contract: ContractData;
    groups: GroupData[];
}

export interface ContractUserData {
    users: Coworker[];
}

export interface EmailEmployee {
    email: string;
    primary: boolean;
    pro: boolean;
    validated_at: string;
}

export interface PhoneEmployee {
    phone: string;
    primary: boolean;
    pro: boolean;
    validated_at: string;
}

export interface IDEmployee {
    id: string;
    firstname: string;
    lastname: string;
    created_at: string;
    language: Language;
    emails: EmailEmployee[];
    phones: PhoneEmployee[];
    core: {
        class: string;
        consumer: number;
        created_at: string;
        id: number;
        token: string;
        uuid: string;
    }[];
    roles: IDRole[];
    apps: {
        app: string;
        tag: string;
        type: string;
        url: string;
        uuid: string;
    }[];
    groups: IDGroup[];
    contracts: IDContract[];
    puppeteer?: {
        uuid: string;
    };
}

export enum IDNameRole {
    PRINCIPAL = 'PRINCIPAL',
    BOOKER = 'BOOKER',
    RIDER = 'RIDER',
}

export interface IDGroup {
    uuid: string;
    name: string;
    contract: IDContract;
    created_at: number;
    roles: IDGroupRole[];
}

export interface IDGroupRole {
    uuid: string;
    role: IDNameRole;
    since: number;
}

export interface IDContract {
    uuid: string;
    name: string;
    created_at?: number;
    signed_at?: number;
    plan: IDPlan;
}

export enum IDContractType {
    CORPORTATE = 'CORPORATE',
    CGU = 'CGU', // CGU = B2C
}

export interface IDPlan {
    uuid?: string;
    name?: string;
    type: IDContractType;
}

export interface Guest {
    firstname: string;
    lastname: string;
    email?: string;
    phone_e164: string;
    language: 'fr' | 'en';
}

export interface UserSettings {
    type?: 'user-settings';
    default_cost_centers?: { group_id: string; cost_center_id: string; cost_center: CostCenter }[];
    default_addresses?: DefaultAddresses;
    default_addresses_pro?: Array<DefaultAddresses & { group_id: string }>;
    default_dispatch_group_id?: string;
    driver_note?: string;
}
export interface GroupSettings {
    default_addresses: DefaultAddresses;
    type: 'group-settings';
    tip_not_allowed?: boolean;
    travel_reason_mandatory?: boolean;
    book_for_group_members?: boolean;
    book_for_group_guests?: boolean;
    max_group_members?: number;
    default_dispatch_group_id?: string;
}

export type PerMode = 'per_ride' | 'per_group' | 'per_user';

export interface DispatchGroup {
    id: string;
    name: string;
    is_authorized: boolean;
}

export interface Policy {
    type: 'user' | 'group' | 'contract' | 'plan';
    entity_id: string; // uuid
    revision?: number;
    error?: string;

    favorites?: Favorite[];
    restrictions?: {
        time_slots?: policy.TimeSlots;
        areas?: policy.GeoRestriction[];
        quantities?: any; // ??
    };

    cost_centers?: CostCenter[];
    cost_center_mandatory?: boolean;
    default_cost_center_mode?: PerMode;
    no_default_cost_center?: boolean;

    default_pickup_mode?: PerMode;
    default_dropoff_mode?: PerMode;

    recurring_enable?: boolean;

    skip_member_invitation?: boolean;

    // To be confirmed
    allowed_rides_types?: [
        {
            asap?: boolean;
            free_ride?: boolean;
            provision?: number; // 0 necessary means it is not permited
            authorized?: boolean; // to work in permited/forbiden mode
        },
    ];
    disabled_dispatch_groups?: string[]; // uuid of disabled dispatch group
    // payment: {}
    dispatch_groups?: DispatchGroup[];
    tip_not_allowed?: boolean;
    travel_reason_mandatory?: boolean;
    book_for_group_members?: boolean;
    book_for_group_guests?: boolean;
    max_group_members?: number;

    default_settings?: UserSettings | GroupSettings;
}

export type DefaultAddresses = {
    pickup?: geo.Address;
    dropoff?: geo.Address;
};

export interface BillSetting {
    vat_number: string;
    purchase_order: string;
    billing_address: {
        street: string;
        city: string;
        postal_code: string;
        country: string;
    };
    billing_emails: string[];
}

//= ===========================//
//= ========== API ============//
//= ===========================//

export enum ApiType {
    ID = 'ID',
    PRIVAPI = 'PRIVAPI',
}

// Pagination
export interface PageInfos {
    num_per_page: number;
    page: number;
}

interface SearchQueryParams {
    company_id?: string;
    booker_id?: string;
    payload_id?: string;
    status?: Status[];
}

export interface EmptyInput {}

// USERS

export interface GetUsersFamilyInput {
    user_id: string;
}

export interface GetCompaniesEmployeesInput {
    company_id: string;
}

// POLICIES

export interface GetPlanInput {
    uuid: string;
}

export interface GetPoliciesInput {
    plans?: string[];
    contracts?: string[];
    groups?: string[];
    users?: string[];
}

export interface GetPoliciesOutput {
    group?: Policy[];
    user?: Policy[];
}

export interface UpdatePolicyInput extends Omit<Policy, 'type' | 'entity_id'> {
    groups?: string[];
    users?: string[];
}

export interface DeletePolicyInput {
    groups?: string[];
    users?: string[];
}

// SELF SERVICE

export interface HasContractUnsignedOutput {
    contract_uuid: string;
    company_group_uuid: string;
    company_name: string;
    company_group_name: string;
}

export interface GetResumeSelfServiceOutput {
    plan_selected: string;
    company: {
        name: string;
        address: AddressDetails;
    };
    contract: ContractSelfService;
    policy: Policy;
    payment_policy: PaymentGroupSetting[];
    payment_methods: PaymentCard[];
    // last_step: SelfServiceStep;
}

export enum SelfServiceStep {
    STARTED = 'STARTED',
    PLAN_SELECTED = 'PLAN_SELECTED',
    COMPANY_INFO_SENT = 'COMPANY_INFO_SENT',
    TRAVEL_POLICY_VALIDATED = 'TRAVEL_POLICY_VALIDATED',
    CARD_ADDED = 'CARD_ADDED',
    PAYMENT_SETUP_DONE = 'PAYMENT_SETUP_DONE',
    SIGNED = 'SIGNED',
}

export interface LogStepSelfServiceInput {
    step: SelfServiceStep;
    payload?: any;
}

export interface CreateSelfServiceContractInput {
    company_name: string;
    plan_id: string;
    billing_address: AddressDetails;
}
export interface UpdateSelfServiceContractInput extends CreateSelfServiceContractInput {
    contractId: string;
}
export interface SignSelfServiceContractInput {
    contractId: string;
}

export interface GetSelfServiceContractInfoInput {
    contractId: string;
}
export interface GetSelfServiceContractInfoOutput {
    contractData: {
        title: string;
        id: string;
        type: string;
        planId: string;
        planName: string;
        dueAmount: number;
        dueDate: number;
        nbCoworkers: number;
        exceedingCoworkers: number;
        exceedingCoworkersCost: number;
        nbGuests: number;
        exceedingGuests: number;
        exceedingGuestsCost: number;
    };
    plansData: {
        name: string;
        maxUsers: number;
        price: {
            subscription?: number;
            extraGuest?: number;
            extraUser?: number;
        };
    }[];
}

// FAVORITES

export interface GetFavoritesInput {
    id?: string;
}

export interface EditFavoriteInput extends Address {
    id: string;
}

export interface DeleteFavoriteInput {
    id: string;
}

export interface CreateFavoriteInput {
    type: string;
    entity_id: string;
    favorites: Address[];
}

// EVENTS
export interface GetEventInput {
    event: 'wicked';
}
export interface GetEventOutput {
    hasAccess: boolean;
    event: string;
}

// BILLS
export interface DownloadBillInput {
    id: string;
    type: string; // 'customer' | 'partner' | 'ride_note'
}

export interface DownloadReportInput {
    id: string;
}

export interface SearchBillsInput {
    id: string;
    bill_number?: string;
    group_ids?: string[];
    sort?: string;
    order?: Order;
    after?: number;
    before?: number;
}

export interface GetBillsSettingsInput {
    id: string;
}
export interface UpdateBillsSettingsInput extends BillSetting {
    id: string;
}

// PROMOTIONS
export interface CreatePromotionInput {
    promo_code: string;
}

// RIDERS
export interface DeleteRiderInput {}

// RIDES
export type Order = 'asc' | 'desc';

export interface GetRidesInput {
    order_date?: Order;
    timestamp_min?: number;
    timestamp_max?: number;
    fetch_all_rides_for_status?: Status[];
    rider_ids?: string[];
    booker_ids?: string[];
    status?: Status[];
    pagination?: PageInfos;
    export_format?: 'csv';
}

export interface GetRideInput {
    id: string;
}

export interface GetSimilarRideInput {
    riderId: string;
    pickupLat: number;
    pickupLng: number;
    dropoffLat: number;
    dropoffLng: number;
    plannedStartDate: number;
}
export interface SOSRideInput {
    id: string;
}

export interface GetTagsOuput {
    tags: string[];
    notes: {
        threshold: number;
        message: string;
    }[];
}
export interface GetCancelReasonsOuput {
    reasons: Record<string, string[]>;
}

export interface EditRideInput extends Omit<CreateRideInput, 'quote_id'> {
    id: string;
    connection_number?: string;
    meeting_point_id?: number;
    payment_method_id?: string;
    tip_amount?: number;
    driver_rating_tags?: string[];
    driver_rating_score?: number;
    driver_rating_comment?: string;
}

export interface CancelRideInput {
    id: string;
    cancel_reason_id?: string;
}

export interface CancelRideOutput {
    ride_id: string;
    core_res: {
        status: string;
        ride: string;
        cancelledEntity: {
            uuid_core: string;
            id_core: number;
            core_date: string;
            type: string;
            flag: string;
            cancel_reason: string;
            cancel_description: string;
        };
        charges: {
            cancelled_booking_id: number;
            base_price: number;
            credit_used: number;
            promo_amount: number;
            net_price: number;
        };
    };
    rideUpdate: {
        status: Status;
        price_details: {
            client_final_price: number;
            client_pre_tax_price: number;
            driver_price: number;
            driver_price_tax: number;
            base_price: number;
            surge: number;
            pricing_request_id: string;
            pricing_response_id: string;
            estimated_distance: number;
            estimated_time: number;
            core_price_confirmation_id: number;
            currency: string;
            voucher: number;
            covered_by_third_party: number;
            cancellation_charge: number;
        };
    };
}
export interface DeleteRideInput {
    id: string;
}
export interface CreateRideInput {
    quote_id: string;
    driver_note?: string;
    ride_reason?: string;
    payment_method_id?: string;
    riders?: BaseUser[];
    booking_platform?: string;
    meeting_point_id?: number;
    cost_center_id?: string;
    nameboard_option?: boolean;
    voucher_id?: string;
    force_duplicate?: boolean; // LT-1809: bypass ride-manager's similar-ride guard when the rider confirms the warning
}

export interface CreateRideRecurrentInput extends CreateRideInput {
    schedule: string; // cron string "30 10 * * 1"
}

export interface UpdateRideRecurrentInput {
    id: string;
    enabled: boolean;
}

export interface DeleteRideRecurrentInput {
    id: string;
}
export interface PauseRecurrentInput {
    recurring_id: string;
    start_date: number;
    end_date: number;
}

export interface UpdatePauseRecurrentInput {
    break_id: string;
    start_date: number;
    end_date: number;
}

export interface UnPauseRecurrentInput {
    break_id: string;
}

export interface ListRideRecurrentInput {
    enabled?: boolean;
    pagination?: PageInfos;
}

// LOCATION

export interface GetLocationInput {
    id?: string; // Added to make the query key unique
    address?: string;
    location?: Location;
}

export interface GetClosestLocationInput {
    location: Location;
}
export interface GetClosestLocationV2Input {
    placeId: string;
}

export interface GetJobHistoryInput {
    id: string;
}

export type GetReservationsInput = SearchQueryParams;

export interface GetReservationInput extends GetReservationsInput {
    id: string;
}

export interface GetServicesInput {
    planned_start_date?: number;
    stops: BaseStop[];
    group_id: string;
}

export interface GetServicesEtaInput {
    start: BaseStop;
}

export interface GetServicesOutput {
    quotes: Quote[];
    encoded_route: string;
    route?: RouteInfos;
    vouchers?: QuoteVoucher[];
}

export interface GetServicesEtaOutput {
    service_uuid: string;
    status: ServiceStatus;
    eta: number;
}

export interface GetPointsOfInterestInput {}

// ID Profile / Auth
export interface VerifyInput {
    otp: string;
}

export interface LostPasswordInput {
    email: string;
    callback?: string;
}

export interface PhoneProfile {
    number: string;
    country: string;
}

export interface EditProfileInput {
    firstname?: string;
    lastname?: string;
    language?: string;
    phone?: PhoneProfile;
    optin?: boolean;
}

export interface GetUserOptinInput {
    id: string;
}

// CONTRACT ID
export interface GetContractInfosInput {
    id: string;
}

export interface ContractInfos {
    uuid: string;
    name: string;
    plan: IDPlan;
    is_enabled: number;
    created_at: number;
    signed_at?: number;
    headcount?: number;
    invitationcount?: number;
}

export interface GetGroupsInput {
    id: string;
}

export enum UserType {
    COWORKER = 'coworker',
    GUEST = 'guest',
    INVITE = 'invite',
}

export type CollabSortKey = 'name' | 'joined_at' | 'firstname' | 'lastname';

export interface SearchGroupCollabsInput {
    id: string;
    user_types: UserType[];
    pagination?: PageInfos;
    pattern?: string;
    groups?: string[];
    not_groups?: string[];
    roles?: string[];
    user_uuids?: string[];
    sort?: CollabSortKey;
    order?: Order;
}

export interface ExportContractUsersInput {
    contractId: string;
}

export interface ExportContractGroupUsersInput extends ExportContractUsersInput {
    groupId: string;
}

export interface GetContractTextInput {
    contractId: string;
}

export interface BaseUserId extends Omit<BaseUser, 'status'> {
    owner: boolean;
    user_type: UserType;
    invited_at?: number;
    joined_at?: number;
    groups: {
        id: string;
        name: string;
        roles: IDGroupRole[];
    }[];
}

export interface DeleteMemberFamilyInput {
    id: string;
    memberId: string;
}

export interface UpdateMemberFamilyInput extends CreateMemberFamilyInput {
    memberId: string;
}

export interface CreateMemberFamilyInput {
    id: string; // contract ID
    firstname: string;
    lastname: string;
    nickname?: string;
    phone?: string;
}

export interface GetGroupDetailInput extends GetGroupsInput {
    groupId: string;
}
export interface CreateGroupInput extends GetGroupsInput {
    id: string;
    name?: string;
}

export interface EditGroupInput extends GetGroupsInput {
    name: string;
    groupId: string;
}

export interface DeleteGroupInput extends GetGroupsInput {
    groupId: string;
}

export interface SendNewInviteInput extends GetGroupsInput {
    groupId: string;
    userId: string;
}

export interface CreateUserInput extends GetGroupsInput {
    groupId: string;
    users: {
        firstname: string;
        lastname: string;
        email: string;
        roles: string[];
    }[];
}

export interface DeleteUserInput extends GetGroupsInput {
    users: string[]; // user uuids
}

export interface AddUserToGroupInput extends GetGroupsInput {
    groupId: string;
    users: {
        uuid: string;
        roles: string[];
    }[];
}

export interface UpdateUser {
    firstname: string;
    lastname: string;
    userId: string;
    group: string;
    contract: string;
    phone: { number: string; country: string };
}

export interface CreateAndAddUserToGroupsInput extends GetGroupsInput {
    users: {
        firstname: string;
        lastname: string;
        email: string;
        phone?: { number: string; country?: string };
        groups: {
            uuid: string;
            roles: string[];
        }[];
    }[];
}

export interface DeleteUserFromGroupInput extends GetGroupsInput {
    groupId: string;
    userId: string;
}

// DROPIN
export interface DropinInput {
    url: string;
    data?: Record<string, unknown>;
}

// PAYMENT
export interface GetPaymentCardsInput {
    group_id?: string;
    contract_id?: string;
}
export interface GetPaymentGroupSettingsInput {
    groups?: string[];
    contract_id?: string;
}

export interface UpdatePaymentGroupPolicyInput {
    group_payment_policies: Omit<PaymentGroupSetting, 'user_id'>[];
}

export interface SetDefaultPaymentCardInput {
    id: string;
    group_id: string;
}

export interface EditPaymentCardInput {
    id: string;
    name: string;
}

export interface DeletePaymentCardInput {
    id: string;
}

export type VoucherValueType = 'percentage' | 'fixed';

// LT-1840: coverage ("prise en charge") defined on a contract. Not to be confused with `Voucher`,
// the booking-flow voucher a quote carries in `elligible_vouchers`.
export interface CoverageVoucher {
    id: string;
    name: string | null;
    type: 'reste_a_charge'; // owned by the back, never sent
    // A share of the ride price or an amount in euros, depending on `value_type`
    value: number;
    value_type: VoucherValueType;
    version: number; // bumped on every PUT
    created_by: string;
    deleted_by?: string;
    created_at: string;
    updated_at?: string;
}

export interface GetVouchersInput {
    contract_id: string;
}

export interface CreateVoucherInput {
    contract_id: string;
    name: string;
    value: number; // > 0, and <= 100 when value_type is 'percentage'
    value_type: VoucherValueType;
}

// PUT is a full replacement, so every field has to be sent back, id excepted
export interface UpdateVoucherInput extends CreateVoucherInput {
    id: string;
}

// A coverage is scoped to its contract: the id alone doesn't locate one
export interface DeleteVoucherInput {
    id: string;
    contract_id: string;
}

// INFO FLIGHT
export interface GetFlightInfoInput {
    stop: {
        address: {
            type: 'AIRPORT';
            poi_id: number;
        };
        connection_details: {
            number: string;
        };
    };
    planned_start_date: number; // timestand expected landing
}

export interface FlightInfoOutput {
    details: {
        core_trip_connection_id: number;
        number: string;
        type: 'FLIGHT' | string;
        company: string;
        connection_time: number; // timestamp
    };
    meetingPointId: number;
}

export interface GetTripPlanningInput {
    start: Location;
    end: Location;
    planned_arrival_date: number; // timestamp
}

export interface TripPlanningOutput {
    planned_start_date: number; // timestamp
    planned_arrival_date: number; // timestamp
    duration: number; // secondes
}

// GUEST
export interface CreateCompanyGuestInput {
    id: string;
    groupId: string;
    guests: Guest[];
}

export interface CreateCompanyGuestOutput {
    processed: CompanyGuest[];
    discarded: CompanyGuest[];
    errors: CompanyGuest[];
}

export interface DeleteCompanyGuestInput {
    guestId: string;
}

export interface UpdateCompanyGuestInput {
    guestId: string;
    firstname: string;
    lastname: string;
    email?: string;
    phone?: string;
}

export interface CompanyGuest {
    firstname: string;
    lastname: string;
    phone_e164: string;
    language: string;
    id_inviter: string;
    id_group: string;
    id_contract: string;
    email: string;
    id: string;
}

// SUPPORT
export interface GetFaqTicketing {
    id: string;
}

export interface CreateTicketInput {
    faq_key: string;
    description: string;
    meta_data: Record<string, unknown>; // Object with meta_data
    attachments?: {
        id: number;
        name: string;
        description?: string;
    }[];
}

export interface UploadFileTicketInput {
    file: any[];
}

export interface FileUpload {
    file: any;
    id: number;
    src: string;
}

// Events
export enum EventType {
    WICKED = 'WICKED',
}
